import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '../components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
    EnvelopeSimple,
    Phone,
    Tag,
    Calendar,
    MapPin,
    Storefront,
    UserCircle,
    CheckCircle,
    Warning,
    PaperPlaneTilt,
    ArrowsCounterClockwise,
    PencilSimple,
    FloppyDisk,
    X as XIcon,
    Receipt,
    WhatsappLogo,
    ChatCircleDots,
    EnvelopeOpen,
    Eye,
    XCircle,
    SpinnerGap,
    IdentificationCard,
    NotePencil,
    CaretDown,
    Plus,
    ClockCounterClockwise,
    FilePdf,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
    getCustomerDetail,
    editCustomer,
    addCustomerNote,
    markCertificateUsed,
    unmarkCertificateUsed,
    resendCertificate,
    cancelCertificate,
    cancelSale,
} from '../services/directoryApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtMoney = (n) =>
    new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
    }).format(n || 0);

const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: 'short', year: 'numeric',
        });
    } catch { return iso.slice(0, 10); }
};

const fmtDateTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const STATUS_META = {
    active: { label: 'Activo', bg: 'bg-[#D1FAE5]', fg: 'text-[#047857]', dot: '#047857' },
    expired: { label: 'Vencido', bg: 'bg-[#FEE2E2]', fg: 'text-[#B91C1C]', dot: '#B91C1C' },
    used: { label: 'Usado', bg: 'bg-[#E0E7FF]', fg: 'text-[#3730A3]', dot: '#3730A3' },
    transferred: { label: 'Transferido', bg: 'bg-[#FEF3C7]', fg: 'text-[#92400E]', dot: '#92400E' },
    cancelled: { label: 'Cancelado', bg: 'bg-[#F3F4F6]', fg: 'text-[#6B7280]', dot: '#6B7280' },
};

const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.active;
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${m.bg} ${m.fg} text-[11px] font-semibold uppercase tracking-wider`}
            data-testid={`status-${status}`}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: m.dot }} />
            {m.label}
        </span>
    );
};

const NOTE_SOURCE_META = {
    cart: { label: 'POS', color: '#1F2D4B', icon: Receipt },
    directory: { label: 'Atención', color: '#FB7185', icon: NotePencil },
    refund: { label: 'Devolución', color: '#6B7280', icon: XCircle },
    audit: { label: 'Edición', color: '#92400E', icon: PencilSimple },
    erp: { label: 'ERP', color: '#3730A3', icon: ChatCircleDots },
};

// ============== INNER: certificate card with actions ==============
const CertCard = ({ cert, canEdit, canRevert, canRefund, canDownloadPdf, onChanged }) => {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null); // action name being processed
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelReason, setCancelReason] = useState('');

    const doDownloadPdf = async () => {
        setBusy('download-pdf');
        try {
            const res = await axios.get(
                `${API}/customer-directory/sale-items/${cert.item_id}/certificate-pdf`,
                { headers: authHeaders(), responseType: 'blob' }
            );
            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url; a.download = `certificado-${cert.folio}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success('PDF descargado');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo descargar el PDF');
        } finally { setBusy(null); }
    };

    const doResend = async (channel, type) => {
        setBusy(`resend-${channel}-${type}`);
        try {
            const res = await resendCertificate(cert.item_id, channel, type);
            if (res.data.status === 'mocked') {
                toast.warning(`${channel.toUpperCase()} en modo demo (no se configuró el proveedor)`);
            } else {
                toast.success(`${type === 'receipt' ? 'Recibo' : 'Certificado'} enviado por ${channel}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || `Error al enviar ${channel}`);
        } finally {
            setBusy(null);
        }
    };

    const doMarkUsed = async () => {
        setBusy('mark-used');
        try {
            await markCertificateUsed(cert.item_id);
            toast.success(`${cert.folio} marcado como usado`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo marcar como usado');
        } finally { setBusy(null); }
    };

    const doUnmark = async () => {
        setBusy('unmark-used');
        try {
            await unmarkCertificateUsed(cert.item_id);
            toast.success(`${cert.folio} reactivado`);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo revertir');
        } finally { setBusy(null); }
    };

    const doCancel = async () => {
        if (!cancelReason.trim()) {
            toast.error('Debes capturar un motivo de cancelación');
            return;
        }
        setBusy('cancel');
        try {
            const res = await cancelCertificate(cert.item_id, cancelReason.trim());
            toast.success(`Devolución ${res.data.refund.refund_folio} generada — ${fmtMoney(res.data.refund.total_refund)}`);
            setConfirmCancel(false);
            setCancelReason('');
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo cancelar');
        } finally { setBusy(null); }
    };

    const channelButton = (channel, type, Icon, label) => (
        <button
            type="button"
            onClick={() => doResend(channel, type)}
            disabled={busy === `resend-${channel}-${type}`}
            data-testid={`resend-${type}-${channel}-${cert.folio}`}
            className="h-8 px-2.5 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B] text-[11px] font-medium text-[#4B5878] flex items-center gap-1 disabled:opacity-50"
            title={`Reenviar ${type === 'receipt' ? 'recibo' : 'certificado'} por ${label}`}
        >
            {busy === `resend-${channel}-${type}`
                ? <SpinnerGap size={12} className="animate-spin" />
                : <Icon size={12} weight="duotone" />}
            {label}
        </button>
    );

    return (
        <>
            <div className="border border-[#ECE3D2] rounded-xl bg-white"
                data-testid={`cert-${cert.folio}-${cert.item_id}`}>
                {/* Header (always visible) */}
                <button
                    type="button"
                    onClick={() => setOpen(!open)}
                    className="w-full text-left p-4 flex items-start justify-between gap-3 hover:bg-[#F9F7F2] transition-colors rounded-t-xl"
                >
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <Tag size={14} weight="duotone" className="text-[#FB7185]" />
                            <span className="font-mono text-sm font-bold text-[#1F2D4B]">{cert.folio}</span>
                            <StatusBadge status={cert.status} />
                        </div>
                        <p className="font-display text-base sm:text-lg font-semibold text-[#1F2D4B] mt-1 truncate">
                            {cert.product_name}
                        </p>
                        <p className="text-[11px] text-[#8B8474] mt-0.5">
                            Compra {fmtDate(cert.purchased_at)} · Vence {fmtDate(cert.expires_at)}
                            {cert.status === 'active' && cert.days_left >= 0 && ` (${cert.days_left}d)`}
                        </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                        <p className="font-mono text-base font-bold text-[#1F2D4B]">{fmtMoney(cert.unit_price)}</p>
                        <CaretDown size={14} className={`text-[#8B8474] ml-auto mt-1 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </div>
                </button>

                {/* Expanded body */}
                {open && (
                    <div className="border-t border-[#ECE3D2] px-4 pb-4 pt-3 space-y-3" data-testid={`cert-detail-${cert.folio}`}>
                        {/* Meta grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px]">
                            <div>
                                <p className="text-[#8B8474] uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                    <Storefront size={10} weight="duotone" /> Sucursal
                                </p>
                                <p className="text-[#1F2D4B] font-medium">{cert.location_name}</p>
                            </div>
                            <div>
                                <p className="text-[#8B8474] uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                    <UserCircle size={10} weight="duotone" /> Vendedor
                                </p>
                                <p className="text-[#1F2D4B] font-medium">{cert.seller_name}</p>
                            </div>
                            <div>
                                <p className="text-[#8B8474] uppercase tracking-wider mb-0.5">Pago</p>
                                <p className="text-[#1F2D4B] font-medium capitalize">{cert.payment_method || '—'}</p>
                            </div>
                            {cert.destination && (
                                <div>
                                    <p className="text-[#8B8474] uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                        <MapPin size={10} weight="duotone" /> Destino
                                    </p>
                                    <p className="text-[#1F2D4B] font-medium">{cert.destination}</p>
                                </div>
                            )}
                        </div>

                        {/* Status-specific info */}
                        {cert.status === 'used' && cert.used_at && (
                            <div className="text-[11px] bg-[#E0E7FF]/40 text-[#3730A3] rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                                <CheckCircle size={13} weight="duotone" />
                                Canjeado el {fmtDateTime(cert.used_at)}{cert.used_by_name && ` por ${cert.used_by_name}`}
                            </div>
                        )}
                        {cert.status === 'cancelled' && (
                            <div className="text-[11px] bg-[#F3F4F6] text-[#6B7280] rounded-md px-2.5 py-1.5 flex items-center gap-2 flex-wrap">
                                <XCircle size={13} weight="duotone" />
                                <span>Cancelado el {fmtDateTime(cert.cancelled_at)}{cert.cancelled_by_name && ` por ${cert.cancelled_by_name}`} · Folio devolución <b>{cert.refund_folio}</b>
                                {cert.cancelled_reason && <span className="text-[#9CA3AF] italic"> · {cert.cancelled_reason}</span>}</span>
                                {cert.refund_folio && (
                                    <div className="flex items-center gap-1.5 ml-auto">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await axios.get(`${API}/refunds/${cert.refund_folio}/receipt-pdf`, { headers: authHeaders(), responseType: 'blob' });
                                                    const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                                    const a = document.createElement('a');
                                                    a.href = url; a.download = `cancelacion-${cert.refund_folio}.pdf`;
                                                    document.body.appendChild(a); a.click(); a.remove();
                                                    URL.revokeObjectURL(url);
                                                } catch { toast.error('No se pudo generar el comprobante'); }
                                            }}
                                            data-testid={`refund-pdf-${cert.refund_folio}`}
                                            className="h-6 px-2 text-[10px] rounded border border-[#6B7280]/30 hover:bg-white"
                                        >Comprobante PDF</button>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    await axios.post(`${API}/refunds/${cert.refund_folio}/resend`, { channel: 'email' }, { headers: authHeaders() });
                                                    toast.success('Comprobante enviado por correo');
                                                } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); }
                                            }}
                                            data-testid={`refund-resend-email-${cert.refund_folio}`}
                                            className="h-6 px-2 text-[10px] rounded border border-[#6B7280]/30 hover:bg-white"
                                        >Reenviar email</button>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    await axios.post(`${API}/refunds/${cert.refund_folio}/resend`, { channel: 'whatsapp' }, { headers: authHeaders() });
                                                    toast.success('Comprobante enviado por WhatsApp');
                                                } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); }
                                            }}
                                            data-testid={`refund-resend-wa-${cert.refund_folio}`}
                                            className="h-6 px-2 text-[10px] rounded border border-[#6B7280]/30 hover:bg-white"
                                        >WhatsApp</button>
                                    </div>
                                )}
                            </div>
                        )}
                        {cert.status === 'transferred' && cert.transferred_to_email && (
                            <div className="text-[11px] bg-[#FEF3C7]/50 text-[#92400E] rounded-md px-2.5 py-1.5 flex items-center gap-1.5">
                                <PaperPlaneTilt size={13} weight="duotone" />
                                Transferido a <b>{cert.transferred_to_email}</b> el {fmtDateTime(cert.transferred_at)}
                            </div>
                        )}

                        {/* Transfer history */}
                        {cert.transfer_history && cert.transfer_history.length > 0 && (
                            <details className="text-[11px]">
                                <summary className="cursor-pointer text-[#4B5878]">
                                    Historial de transferencias ({cert.transfer_history.length})
                                </summary>
                                <ul className="mt-1.5 space-y-1 pl-3 border-l border-[#ECE3D2]">
                                    {cert.transfer_history.map((h, i) => (
                                        <li key={i} className="text-[#4B5878]">
                                            {fmtDate(h.transferred_at)} · <span className="line-through">{h.from_email}</span> → <b>{h.to_email}</b>
                                        </li>
                                    ))}
                                </ul>
                            </details>
                        )}

                        {/* Actions */}
                        <div className="pt-3 border-t border-[#ECE3D2] space-y-2.5">
                            {/* Open public cert link */}
                            <div className="flex flex-wrap gap-2">
                                <a
                                    href={`/cert/${cert.folio}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`view-cert-${cert.folio}`}
                                    className="h-8 px-3 rounded-md bg-[#1F2D4B] hover:bg-[#2E3F60] text-white text-[11px] font-semibold flex items-center gap-1.5"
                                >
                                    <Eye size={12} weight="bold" /> Ver certificado
                                </a>
                                <a
                                    href={`/tickets/${cert.sale_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`view-receipt-${cert.folio}`}
                                    className="h-8 px-3 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B] text-[#4B5878] text-[11px] font-semibold flex items-center gap-1.5"
                                >
                                    <Receipt size={12} weight="bold" /> Ver recibo
                                </a>

                                {/* Quick actions per status */}
                                {cert.status === 'active' && canEdit && (
                                    <button
                                        type="button"
                                        onClick={doMarkUsed}
                                        disabled={busy === 'mark-used'}
                                        data-testid={`mark-used-${cert.folio}`}
                                        className="h-8 px-3 rounded-md bg-[#047857] hover:bg-[#065F46] text-white text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                        {busy === 'mark-used'
                                            ? <SpinnerGap size={12} className="animate-spin" />
                                            : <CheckCircle size={12} weight="bold" />}
                                        Marcar usado
                                    </button>
                                )}
                                {cert.status === 'used' && canRevert && (
                                    <button
                                        type="button"
                                        onClick={doUnmark}
                                        disabled={busy === 'unmark-used'}
                                        data-testid={`unmark-used-${cert.folio}`}
                                        className="h-8 px-3 rounded-md border border-[#ECE3D2] hover:bg-[#F8F4EC] text-[#4B5878] text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50"
                                    >
                                        {busy === 'unmark-used'
                                            ? <SpinnerGap size={12} className="animate-spin" />
                                            : <ArrowsCounterClockwise size={12} weight="bold" />}
                                        Revertir
                                    </button>
                                )}
                                {cert.status === 'active' && canRefund && (
                                    <button
                                        type="button"
                                        onClick={() => setConfirmCancel(true)}
                                        data-testid={`cancel-cert-${cert.folio}`}
                                        className="h-8 px-3 rounded-md border border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2] text-[11px] font-semibold flex items-center gap-1.5"
                                    >
                                        <XCircle size={12} weight="bold" /> Cancelar
                                    </button>
                                )}
                                {cert.status === 'expired' && (
                                    <span className="text-[11px] text-[#B91C1C] flex items-center gap-1.5 px-2 py-1">
                                        <Warning size={13} weight="duotone" /> Vencido — no canjeable
                                    </span>
                                )}
                            </div>

                            {/* Resend buttons */}
                            {(cert.status === 'active' || cert.status === 'transferred') && (
                                <div className="space-y-1.5">
                                    <p className="text-[10px] uppercase tracking-wider text-[#8B8474]">Reenviar certificado</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {channelButton('whatsapp', 'certificate', WhatsappLogo, 'WhatsApp')}
                                        {channelButton('sms', 'certificate', ChatCircleDots, 'SMS')}
                                        {channelButton('email', 'certificate', EnvelopeOpen, 'Correo')}
                                        {canDownloadPdf && (
                                            <button
                                                type="button"
                                                onClick={doDownloadPdf}
                                                disabled={busy === 'download-pdf'}
                                                data-testid={`download-cert-pdf-${cert.folio}`}
                                                className="h-8 px-2.5 rounded-md border border-[#1F2D4B] bg-[#1F2D4B] text-white hover:bg-[#2E3F60] text-[11px] font-semibold flex items-center gap-1 disabled:opacity-50"
                                                title="Descargar PDF del certificado"
                                            >
                                                {busy === 'download-pdf'
                                                    ? <SpinnerGap size={12} className="animate-spin" />
                                                    : <FilePdf size={12} weight="bold" />}
                                                Descargar PDF
                                            </button>
                                        )}
                                    </div>
                                    <p className="text-[10px] uppercase tracking-wider text-[#8B8474] pt-1">Reenviar recibo de compra</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {channelButton('whatsapp', 'receipt', WhatsappLogo, 'WhatsApp')}
                                        {channelButton('sms', 'receipt', ChatCircleDots, 'SMS')}
                                        {channelButton('email', 'receipt', EnvelopeOpen, 'Correo')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Cancel confirm dialog with reason */}
            <AlertDialog open={confirmCancel} onOpenChange={(o) => { if (!o) { setConfirmCancel(false); setCancelReason(''); } }}>
                <AlertDialogContent className="max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-[#B91C1C]">Cancelar certificado {cert.folio}</AlertDialogTitle>
                        <AlertDialogDescription>
                            Se generará un recibo de devolución (DEV-…) por <b>{fmtMoney(cert.unit_price)}</b>.
                            Esta acción se reflejará en el historial del cliente y, más adelante, en el CRM del ERP.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-2">
                        <label className="text-xs font-semibold text-[#4B5878]">Motivo de la cancelación *</label>
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Ej. Cliente cambió de planes / problema de salud / error en el cobro"
                            data-testid={`cancel-reason-${cert.folio}`}
                            className="w-full min-h-[88px] p-2.5 rounded-md border border-[#ECE3D2] text-sm text-[#1F2D4B] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C]"
                        />
                    </div>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`cancel-dialog-no-${cert.folio}`}>Volver</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); doCancel(); }}
                            disabled={busy === 'cancel' || !cancelReason.trim()}
                            data-testid={`cancel-dialog-confirm-${cert.folio}`}
                            className="bg-[#B91C1C] hover:bg-[#991B1B]"
                        >
                            {busy === 'cancel'
                                ? <SpinnerGap size={14} className="animate-spin mr-1" />
                                : <XCircle size={14} weight="bold" className="mr-1" />}
                            Confirmar devolución
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// ============== MAIN SHEET ==============
const CustomerDetailSheet = ({ customerId, open, onOpenChange }) => {
    const { user, can } = useAuth();
    const canEditCustomer = ['admin', 'manager'].includes(user?.role);
    const canMarkUsed = ['admin', 'manager', 'customer_service'].includes(user?.role);
    const canRevertUsed = ['admin', 'manager'].includes(user?.role);
    const canRefund = ['admin', 'manager'].includes(user?.role);
    const canDownloadPdf = user?.role === 'admin' || (can && can('customers.download_certificate_pdf'));

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState('overview');

    // Edit form
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', rfc: '', address: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    // Note form
    const [newNote, setNewNote] = useState('');
    const [postingNote, setPostingNote] = useState(false);

    // Cancel sale dialog
    const [cancelSaleDialog, setCancelSaleDialog] = useState(null); // { sale_id, total }
    const [cancelSaleReason, setCancelSaleReason] = useState('');
    const [cancellingSale, setCancellingSale] = useState(false);

    const reload = async () => {
        if (!customerId) return;
        setLoading(true);
        try {
            const res = await getCustomerDetail(customerId);
            setData(res.data);
            setEditForm({
                name: res.data.customer.name || '',
                email: res.data.customer.email || '',
                phone: res.data.customer.phone || '',
                rfc: res.data.customer.rfc || '',
                address: res.data.customer.address || '',
            });
        } catch (err) {
            toast.error('Error al cargar el cliente');
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (open && customerId) {
            setTab('overview');
            setEditing(false);
            reload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, customerId]);

    const saveEdit = async () => {
        setSavingEdit(true);
        try {
            await editCustomer(customerId, editForm);
            toast.success('Datos del cliente actualizados');
            setEditing(false);
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudieron guardar los cambios');
        } finally { setSavingEdit(false); }
    };

    const postNote = async () => {
        if (!newNote.trim()) return;
        setPostingNote(true);
        try {
            await addCustomerNote(customerId, newNote.trim(), 'directory');
            toast.success('Nota agregada');
            setNewNote('');
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo agregar la nota');
        } finally { setPostingNote(false); }
    };

    const doCancelSale = async () => {
        if (!cancelSaleReason.trim()) {
            toast.error('Captura un motivo');
            return;
        }
        setCancellingSale(true);
        try {
            const res = await cancelSale(cancelSaleDialog.sale_id, cancelSaleReason.trim());
            toast.success(`Devolución ${res.data.refund.refund_folio} generada — ${fmtMoney(res.data.refund.total_refund)}`);
            setCancelSaleDialog(null);
            setCancelSaleReason('');
            await reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'No se pudo cancelar la venta');
        } finally { setCancellingSale(false); }
    };

    const sortedNotes = useMemo(() => {
        const list = data?.customer?.notes || [];
        return [...list].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }, [data]);

    if (!open) return null;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto p-0 bg-[#F4ECE0]"
                data-testid="customer-detail-sheet"
            >
                {loading && !data && (
                    <>
                        <SheetHeader className="sr-only">
                            <SheetTitle>Cargando cliente</SheetTitle>
                            <SheetDescription>Cargando los datos del cliente seleccionado</SheetDescription>
                        </SheetHeader>
                        <div className="flex items-center justify-center h-full">
                            <SpinnerGap size={32} className="animate-spin text-[#FB7185]" />
                        </div>
                    </>
                )}

                {data && (
                    <>
                        {/* Header */}
                        <SheetHeader className="bg-[#1F2D4B] text-white p-6 space-y-2">
                            <SheetTitle className="text-white font-display text-2xl">
                                {data.customer.name}
                            </SheetTitle>
                            <SheetDescription className="text-white/70 text-xs flex flex-wrap gap-x-4 gap-y-1">
                                <span className="flex items-center gap-1"><EnvelopeSimple size={12} /> {data.customer.email || '—'}</span>
                                <span className="flex items-center gap-1"><Phone size={12} /> {data.customer.phone || '—'}</span>
                                {data.customer.rfc && <span className="flex items-center gap-1"><IdentificationCard size={12} /> {data.customer.rfc}</span>}
                            </SheetDescription>
                            {/* Summary chips */}
                            <div className="flex flex-wrap gap-1.5 pt-2">
                                {[
                                    { k: 'active', label: 'activos' },
                                    { k: 'used', label: 'usados' },
                                    { k: 'expired', label: 'vencidos' },
                                    { k: 'transferred', label: 'transferidos' },
                                    { k: 'cancelled', label: 'cancelados' },
                                ].map((c) => (
                                    data.summary[c.k] > 0 && (
                                        <span key={c.k} className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/90">
                                            {data.summary[c.k]} {c.label}
                                        </span>
                                    )
                                ))}
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FB7185]/30 text-white font-semibold">
                                    Neto: {fmtMoney(data.summary.net_spent)}
                                </span>
                            </div>
                        </SheetHeader>

                        {/* Tabs */}
                        <Tabs value={tab} onValueChange={setTab} className="p-6">
                            <TabsList className="grid grid-cols-4 mb-4 bg-white">
                                <TabsTrigger value="overview" data-testid="tab-overview">Datos</TabsTrigger>
                                <TabsTrigger value="notes" data-testid="tab-notes">Notas</TabsTrigger>
                                <TabsTrigger value="certs" data-testid="tab-certs">Certificados</TabsTrigger>
                                <TabsTrigger value="receipts" data-testid="tab-receipts">Recibos</TabsTrigger>
                            </TabsList>

                            {/* ========= OVERVIEW ========= */}
                            <TabsContent value="overview" className="bg-white rounded-2xl p-5 border border-[#ECE3D2]">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">Datos del cliente</h3>
                                    {canEditCustomer && !editing && (
                                        <button
                                            type="button"
                                            onClick={() => setEditing(true)}
                                            data-testid="edit-customer-btn"
                                            className="h-8 px-3 rounded-md border border-[#ECE3D2] hover:border-[#1F2D4B] text-[#4B5878] text-[11px] font-semibold flex items-center gap-1.5"
                                        >
                                            <PencilSimple size={12} weight="bold" /> Editar
                                        </button>
                                    )}
                                </div>
                                {editing ? (
                                    <div className="space-y-3" data-testid="edit-customer-form">
                                        {[
                                            { k: 'name', label: 'Nombre completo' },
                                            { k: 'email', label: 'Correo electrónico', type: 'email' },
                                            { k: 'phone', label: 'Teléfono' },
                                            { k: 'rfc', label: 'RFC' },
                                            { k: 'address', label: 'Dirección' },
                                        ].map((f) => (
                                            <div key={f.k}>
                                                <label className="text-[11px] uppercase tracking-wider text-[#8B8474] font-semibold">{f.label}</label>
                                                <input
                                                    type={f.type || 'text'}
                                                    value={editForm[f.k]}
                                                    onChange={(e) => setEditForm({ ...editForm, [f.k]: e.target.value })}
                                                    data-testid={`edit-${f.k}`}
                                                    className="w-full h-10 px-3 rounded-md border border-[#ECE3D2] text-sm text-[#1F2D4B] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B] mt-1"
                                                />
                                            </div>
                                        ))}
                                        <div className="flex gap-2 pt-2">
                                            <button
                                                type="button"
                                                onClick={saveEdit}
                                                disabled={savingEdit}
                                                data-testid="save-edit-btn"
                                                className="h-9 px-4 rounded-md bg-[#1F2D4B] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                                            >
                                                {savingEdit ? <SpinnerGap size={13} className="animate-spin" /> : <FloppyDisk size={13} weight="bold" />}
                                                Guardar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditing(false)}
                                                className="h-9 px-4 rounded-md border border-[#ECE3D2] text-[#4B5878] text-xs font-semibold flex items-center gap-1.5"
                                            >
                                                <XIcon size={13} /> Cancelar
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <dl className="space-y-2 text-sm">
                                        {[
                                            ['Nombre', data.customer.name],
                                            ['Correo', data.customer.email || '—'],
                                            ['Teléfono', data.customer.phone || '—'],
                                            ['RFC', data.customer.rfc || '—'],
                                            ['Dirección', data.customer.address || '—'],
                                            ['Cliente desde', fmtDate(data.customer.created_at)],
                                        ].map(([k, v]) => (
                                            <div key={k} className="flex items-baseline justify-between gap-3 py-1 border-b border-[#F4ECE0] last:border-b-0">
                                                <dt className="text-[11px] uppercase tracking-wider text-[#8B8474] font-semibold flex-shrink-0">{k}</dt>
                                                <dd className="text-[#1F2D4B] text-right break-words">{v}</dd>
                                            </div>
                                        ))}
                                    </dl>
                                )}

                                <div className="mt-6 pt-5 border-t border-[#ECE3D2] grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {Object.entries({
                                        active: 'Activos', used: 'Usados', expired: 'Vencidos',
                                        transferred: 'Transferidos', cancelled: 'Cancelados',
                                    }).map(([k, label]) => (
                                        <div key={k} className="bg-[#F9F7F2] rounded-md px-3 py-2 text-center">
                                            <p className="font-mono text-lg font-bold" style={{ color: STATUS_META[k].dot }}>
                                                {data.summary[k]}
                                            </p>
                                            <p className="text-[10px] uppercase tracking-wider text-[#8B8474]">{label}</p>
                                        </div>
                                    ))}
                                </div>
                            </TabsContent>

                            {/* ========= NOTES ========= */}
                            <TabsContent value="notes" className="bg-white rounded-2xl p-5 border border-[#ECE3D2]">
                                <h3 className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold mb-3 flex items-center gap-1.5">
                                    <ClockCounterClockwise size={13} weight="duotone" />
                                    Timeline de notas internas
                                </h3>
                                <p className="text-[11px] text-[#8B8474] mb-4">
                                    Estas notas son sólo para el equipo. <b>Nunca</b> se envían al cliente. Se sincronizarán con el CRM del ERP cuando esté integrado.
                                </p>

                                {/* New note */}
                                <div className="bg-[#F9F7F2] rounded-xl p-3 mb-4 border border-[#ECE3D2]">
                                    <textarea
                                        value={newNote}
                                        onChange={(e) => setNewNote(e.target.value)}
                                        placeholder="Agrega una nota… (ej. 'Cliente prefiere viajar en temporada baja, hablar con María')"
                                        data-testid="new-note-input"
                                        className="w-full min-h-[72px] p-2 rounded-md border border-[#ECE3D2] bg-white text-sm text-[#1F2D4B] focus:outline-none focus:ring-2 focus:ring-[#1F2D4B]/20 focus:border-[#1F2D4B]"
                                    />
                                    <div className="flex justify-end mt-2">
                                        <button
                                            type="button"
                                            onClick={postNote}
                                            disabled={postingNote || !newNote.trim()}
                                            data-testid="post-note-btn"
                                            className="h-9 px-4 rounded-md bg-[#1F2D4B] text-white text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50"
                                        >
                                            {postingNote
                                                ? <SpinnerGap size={13} className="animate-spin" />
                                                : <Plus size={13} weight="bold" />}
                                            Publicar nota
                                        </button>
                                    </div>
                                </div>

                                {/* Timeline */}
                                {sortedNotes.length === 0 ? (
                                    <p className="text-sm text-[#8B8474] text-center py-6">Aún no hay notas. Captura la primera.</p>
                                ) : (
                                    <ol className="relative border-l border-[#ECE3D2] ml-2 space-y-4">
                                        {sortedNotes.map((n) => {
                                            const meta = NOTE_SOURCE_META[n.source] || NOTE_SOURCE_META.directory;
                                            const Icon = meta.icon;
                                            return (
                                                <li key={n.id} className="ml-4" data-testid={`note-${n.id}`}>
                                                    <span
                                                        className="absolute -left-[7px] w-3.5 h-3.5 rounded-full ring-2 ring-white flex items-center justify-center"
                                                        style={{ background: meta.color }}
                                                    >
                                                        <Icon size={8} weight="bold" className="text-white" />
                                                    </span>
                                                    <div className="bg-white border border-[#ECE3D2] rounded-md p-3">
                                                        <div className="flex flex-wrap items-center justify-between text-[10px] uppercase tracking-wider text-[#8B8474] mb-1.5">
                                                            <span className="font-semibold" style={{ color: meta.color }}>{meta.label}</span>
                                                            <span>{fmtDateTime(n.created_at)}</span>
                                                        </div>
                                                        <p className="text-sm text-[#1F2D4B] whitespace-pre-wrap">{n.text}</p>
                                                        {n.author_name && (
                                                            <p className="text-[10px] text-[#8B8474] mt-1.5">— {n.author_name}</p>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ol>
                                )}
                            </TabsContent>

                            {/* ========= CERTIFICATES ========= */}
                            <TabsContent value="certs" className="space-y-3">
                                {data.certificates.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-8 text-center border border-[#ECE3D2]">
                                        <p className="text-sm text-[#8B8474]">El cliente aún no tiene certificados.</p>
                                    </div>
                                ) : (
                                    data.certificates.map((c) => (
                                        <CertCard
                                            key={c.item_id}
                                            cert={c}
                                            canEdit={canMarkUsed}
                                            canRevert={canRevertUsed}
                                            canRefund={canRefund}
                                            canDownloadPdf={canDownloadPdf}
                                            onChanged={reload}
                                        />
                                    ))
                                )}
                            </TabsContent>

                            {/* ========= RECEIPTS ========= */}
                            <TabsContent value="receipts" className="space-y-3">
                                {data.receipts.length === 0 ? (
                                    <div className="bg-white rounded-2xl p-8 text-center border border-[#ECE3D2]">
                                        <p className="text-sm text-[#8B8474]">Sin recibos.</p>
                                    </div>
                                ) : (
                                    data.receipts.map((r) => {
                                        const isRefund = r.type === 'refund';
                                        return (
                                            <div
                                                key={`${r.type}-${r.folio_display}-${r.created_at}`}
                                                className={`bg-white rounded-xl border p-4 ${isRefund ? 'border-[#FCA5A5]' : 'border-[#ECE3D2]'} ${r.cancelled ? 'opacity-70' : ''}`}
                                                data-testid={`receipt-${r.folio_display}`}
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <Receipt size={14} weight="duotone" className={isRefund ? 'text-[#B91C1C]' : 'text-[#1F2D4B]'} />
                                                            <span className="font-mono text-sm font-bold text-[#1F2D4B]">{r.folio_display}</span>
                                                            {isRefund && (
                                                                <span className="text-[10px] uppercase tracking-wider bg-[#FEE2E2] text-[#B91C1C] px-2 py-0.5 rounded-full font-semibold">
                                                                    Devolución {r.scope === 'sale' ? '(venta completa)' : '(parcial)'}
                                                                </span>
                                                            )}
                                                            {r.cancelled && !isRefund && (
                                                                <span className="text-[10px] uppercase tracking-wider bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 rounded-full font-semibold">
                                                                    Cancelada
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-[11px] text-[#8B8474] mt-1">
                                                            {fmtDateTime(r.created_at)}
                                                            {!isRefund && r.location_name && ` · ${r.location_name}`}
                                                            {!isRefund && r.seller_name && ` · ${r.seller_name}`}
                                                            {isRefund && r.processed_by_name && ` · Procesó ${r.processed_by_name}`}
                                                        </p>
                                                        {isRefund && r.reason && (
                                                            <p className="text-[11px] text-[#6B7280] italic mt-1">Motivo: {r.reason}</p>
                                                        )}
                                                        {!isRefund && r.refund_folio && (
                                                            <p className="text-[11px] text-[#B91C1C] mt-1">Devolución asociada: <b>{r.refund_folio}</b></p>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className={`font-mono text-base font-bold ${r.total < 0 ? 'text-[#B91C1C]' : 'text-[#1F2D4B]'}`}>
                                                            {r.total < 0 ? '-' : ''}{fmtMoney(Math.abs(r.total))}
                                                        </p>
                                                        {!isRefund && (
                                                            <p className="text-[10px] uppercase text-[#8B8474]">{r.items_count} certif.</p>
                                                        )}
                                                    </div>
                                                </div>

                                                {!isRefund && (
                                                    <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-[#ECE3D2]">
                                                        <a
                                                            href={`/tickets/${r.sale_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            data-testid={`view-sale-receipt-${r.folio_display}`}
                                                            className="h-8 px-3 rounded-md bg-[#1F2D4B] hover:bg-[#2E3F60] text-white text-[11px] font-semibold flex items-center gap-1.5"
                                                        >
                                                            <Eye size={12} weight="bold" /> Ver recibo
                                                        </a>
                                                        {canRefund && !r.cancelled && (
                                                            <button
                                                                type="button"
                                                                onClick={() => setCancelSaleDialog({ sale_id: r.sale_id, total: r.total })}
                                                                data-testid={`cancel-sale-${r.folio_display}`}
                                                                className="h-8 px-3 rounded-md border border-[#B91C1C] text-[#B91C1C] hover:bg-[#FEE2E2] text-[11px] font-semibold flex items-center gap-1.5"
                                                            >
                                                                <XCircle size={12} weight="bold" /> Cancelar venta completa
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </TabsContent>
                        </Tabs>
                    </>
                )}

                {/* Cancel-whole-sale dialog */}
                <AlertDialog open={!!cancelSaleDialog} onOpenChange={(o) => { if (!o) { setCancelSaleDialog(null); setCancelSaleReason(''); } }}>
                    <AlertDialogContent className="max-w-md">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-[#B91C1C]">Cancelar venta completa</AlertDialogTitle>
                            <AlertDialogDescription>
                                Se cancelarán <b>todos los certificados activos</b> de esta venta y se generará un único recibo de devolución (DEV-…).
                                Los certificados ya usados, transferidos o cancelados no se ven afectados.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <div className="space-y-2">
                            <label className="text-xs font-semibold text-[#4B5878]">Motivo *</label>
                            <textarea
                                value={cancelSaleReason}
                                onChange={(e) => setCancelSaleReason(e.target.value)}
                                placeholder="Motivo de la cancelación de la venta"
                                data-testid="cancel-sale-reason"
                                className="w-full min-h-[88px] p-2.5 rounded-md border border-[#ECE3D2] text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C]"
                            />
                        </div>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="cancel-sale-dialog-no">Volver</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={(e) => { e.preventDefault(); doCancelSale(); }}
                                disabled={cancellingSale || !cancelSaleReason.trim()}
                                data-testid="cancel-sale-dialog-confirm"
                                className="bg-[#B91C1C] hover:bg-[#991B1B]"
                            >
                                {cancellingSale
                                    ? <SpinnerGap size={14} className="animate-spin mr-1" />
                                    : <XCircle size={14} weight="bold" className="mr-1" />}
                                Confirmar
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </SheetContent>
        </Sheet>
    );
};

export default CustomerDetailSheet;
